import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Chip, IconButton, MenuItem, Select } from "@mui/material";
import * as React from "react";

import {
  customFieldDisplayName,
  CustomFieldRecord,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { Button } from "@/shared/foundation/primitives";

import { formatCustomFieldValue } from "./customFieldValueFormat";
import { CustomFieldValueInput } from "./CustomFieldValueInput";
import styles from "./CustomFieldValues.module.css";
import { isCommittableValue } from "./customFieldValueValidation";

export interface CustomFieldValuesTableProps {
  entityType: TargetEntityType;
  readyFields: CustomFieldRecord[];

  /** Current values by field name; a `null`/absent value renders no row. */
  values: Record<string, unknown>;

  /** Set (or replace) a field's value. */
  onSet: (fieldName: string, value: unknown) => void;

  /** Clear a field's value. */
  onClear: (fieldName: string) => void;

  /**
   * How the section frames itself against its host. `"card"` renders a
   * self-contained bordered, rounded, padded surface with row separators,
   * `"plain"` drops all of that so the section sits flush inside a
   * borderless list.
   */
  variant: "card" | "plain";
}

interface DraftState {
  fieldName: string;
  value: unknown;
}

function hasValue(values: Record<string, unknown>, fieldName: string): boolean {
  return values[fieldName] !== null && values[fieldName] !== undefined;
}

function defaultValueFor(field: CustomFieldRecord): unknown {
  return field.field_type === CustomFieldType.Boolean ? false : null;
}

/**
 * Editable, expandable table of an entity's custom-field values.
 *
 * Presentational: the host supplies `values` and the `onSet`/`onClear` callbacks.
 */
export function CustomFieldValuesTable({
  entityType,
  readyFields,
  values,
  onSet,
  onClear,
  variant,
}: CustomFieldValuesTableProps) {
  const [editDraft, setEditDraft] = React.useState<DraftState | null>(null);
  const [addDraft, setAddDraft] = React.useState<DraftState | null>(null);

  const entityPlural = `${entityType}s`;

  // Briefly highlight a newly added row, so the eye can follow it to its slot
  // (it doesn't land at the bottom). Decorative; the animation is gated on
  // reduced-motion.
  const [highlightedField, setHighlightedField] = React.useState<string | null>(
    null,
  );
  const highlightTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  React.useEffect(
    () => () => {
      if (highlightTimer.current) {
        clearTimeout(highlightTimer.current);
      }
    },
    [],
  );

  function flashRow(fieldName: string) {
    setHighlightedField(fieldName);
    if (highlightTimer.current) {
      clearTimeout(highlightTimer.current);
    }
    highlightTimer.current = setTimeout(() => setHighlightedField(null), 1600);
  }

  const setFields = readyFields.filter((field) =>
    hasValue(values, field.field_name),
  );

  const unsetFields = readyFields.filter(
    (field) => !hasValue(values, field.field_name),
  );

  function fieldByName(fieldName: string): CustomFieldRecord | undefined {
    return readyFields.find((field) => field.field_name === fieldName);
  }

  function startEdit(field: CustomFieldRecord) {
    setAddDraft(null);
    setEditDraft({
      fieldName: field.field_name,
      value: values[field.field_name],
    });
  }

  function confirmEdit() {
    const field = editDraft && fieldByName(editDraft.fieldName);
    if (editDraft && field && isCommittableValue(field, editDraft.value)) {
      onSet(editDraft.fieldName, editDraft.value);
      setEditDraft(null);
    }
  }

  function openAdd() {
    setEditDraft(null);
    const first = unsetFields[0];
    if (first) {
      setAddDraft({
        fieldName: first.field_name,
        value: defaultValueFor(first),
      });
    }
  }

  function confirmAdd() {
    const field = addDraft && fieldByName(addDraft.fieldName);
    if (addDraft && field && isCommittableValue(field, addDraft.value)) {
      onSet(addDraft.fieldName, addDraft.value);
      flashRow(addDraft.fieldName);
      setAddDraft(null);
    }
  }

  const addField = addDraft && fieldByName(addDraft.fieldName);

  const sectionClassName = `${styles.section} ${styles[variant]}`;

  return (
    <section className={sectionClassName}>
      <div className={styles.headingRow}>
        <h3 className={styles.heading}>Custom Fields</h3>
      </div>

      {readyFields.length === 0 ? (
        <p className={styles.placeholder}>
          No custom fields defined for {entityPlural}.
        </p>
      ) : (
        <>
          {setFields.length === 0 && addDraft === null ? (
            <p className={styles.emptyText}>No custom fields set.</p>
          ) : (
            <table className={styles.table}>
              <tbody>
                {setFields.map((field) => {
                  const editing =
                    editDraft?.fieldName === field.field_name
                      ? editDraft
                      : null;

                  const rowClass =
                    highlightedField === field.field_name
                      ? `${styles.propertyRow} ${styles.rowHighlight}`
                      : styles.propertyRow;

                  return (
                    <tr
                      key={field.field_name}
                      className={rowClass}
                      onKeyDown={
                        editing
                          ? (event) => {
                              if (event.key === "Escape") {
                                event.preventDefault();
                                setEditDraft(null);
                              }
                            }
                          : undefined
                      }
                    >
                      <th scope="row" className={styles.propertyLabel}>
                        {customFieldDisplayName(field)}
                      </th>
                      {editing ? (
                        <>
                          <td className={styles.valueCell}>
                            <div
                              className={`${styles.valueContent} ${styles.valueContentEditing}`}
                            >
                              <CustomFieldValueInput
                                field={field}
                                value={editing.value}
                                onChange={(value) =>
                                  setEditDraft({
                                    fieldName: field.field_name,
                                    value,
                                  })
                                }
                                onCommit={confirmEdit}
                                autoFocus
                              />
                            </div>
                          </td>
                          <td className={styles.actionsCell}>
                            <ConfirmCancel
                              committable={isCommittableValue(
                                field,
                                editing.value,
                              )}
                              onConfirm={confirmEdit}
                              onCancel={() => setEditDraft(null)}
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className={styles.valueCell}>
                            <div className={styles.valueContent}>
                              <ValueDisplay
                                field={field}
                                value={values[field.field_name]}
                              />
                            </div>
                          </td>
                          <td className={styles.actionsCell}>
                            <div className={styles.rowActions}>
                              <IconButton
                                size="small"
                                aria-label={`Edit ${customFieldDisplayName(field)}`}
                                onClick={() => startEdit(field)}
                              >
                                <EditIcon color="primary" fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label={`Clear ${customFieldDisplayName(field)}`}
                                onClick={() => onClear(field.field_name)}
                              >
                                <DeleteIcon
                                  color="secondary"
                                  fontSize="small"
                                />
                              </IconButton>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}

                {addDraft && addField && (
                  <tr
                    className={styles.propertyRow}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        setAddDraft(null);
                      }
                    }}
                  >
                    <td className={styles.pickerCell}>
                      <FieldPicker
                        fields={unsetFields}
                        selected={addDraft.fieldName}
                        onSelect={(fieldName) => {
                          const picked = fieldByName(fieldName);
                          setAddDraft({
                            fieldName,
                            value: picked ? defaultValueFor(picked) : null,
                          });
                        }}
                      />
                    </td>
                    <td className={styles.valueCell}>
                      <div
                        className={`${styles.valueContent} ${styles.valueContentEditing}`}
                      >
                        <CustomFieldValueInput
                          // Remount on picker switch so the Number input's local
                          // text buffer resets. Edit rows get this from the <tr key>.
                          key={addField.field_name}
                          field={addField}
                          value={addDraft.value}
                          onChange={(value) =>
                            setAddDraft({
                              fieldName: addDraft.fieldName,
                              value,
                            })
                          }
                          onCommit={confirmAdd}
                          autoFocus
                        />
                      </div>
                    </td>
                    <td className={styles.actionsCell}>
                      <ConfirmCancel
                        committable={isCommittableValue(
                          addField,
                          addDraft.value,
                        )}
                        onConfirm={confirmAdd}
                        onCancel={() => setAddDraft(null)}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {!addDraft && unsetFields.length > 0 && (
            <div className={styles.footer}>
              <Button
                variant="text"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openAdd}
              >
                Set custom field
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Display rendering for a set value: enums as an outlined chip (a single token,
 * capped with an ellipsis + title tooltip), strings as wrapping text, and the
 * short scalar types (number/boolean/timestamp) as single-line text. */
function ValueDisplay({
  field,
  value,
}: {
  field: CustomFieldRecord;
  value: unknown;
}) {
  if (field.field_type === CustomFieldType.Enum) {
    return (
      <Chip
        className={styles.enumChip}
        label={String(value)}
        title={String(value)}
        size="small"
        variant="outlined"
      />
    );
  }

  const textClassName =
    field.field_type === CustomFieldType.String
      ? styles.valueText
      : `${styles.valueText} ${styles.valueTextSingleLine}`;

  return (
    <span className={textClassName}>
      {formatCustomFieldValue(field, value)}
    </span>
  );
}

/** The explicit ✓ confirm / ✕ cancel buttons shared by inline edit (in its own
 * table cell) and the add row. Confirm is disabled until the draft is valid. */
function ConfirmCancel({
  committable,
  onConfirm,
  onCancel,
}: {
  committable: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.editActions}>
      <IconButton
        size="small"
        className={styles.confirmButton}
        aria-label="Confirm"
        disabled={!committable}
        onClick={onConfirm}
      >
        <CheckIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="Cancel" onClick={onCancel}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

interface FieldPickerProps {
  fields: CustomFieldRecord[];
  selected: string;
  onSelect: (fieldName: string) => void;
}

/** Dropdown of not-yet-set fields, by display name, for the add row. */
function FieldPicker({ fields, selected, onSelect }: FieldPickerProps) {
  return (
    <Select
      size="small"
      fullWidth
      className={styles.fieldPicker}
      value={selected}
      onChange={(event) => onSelect(event.target.value)}
      aria-label="Select a custom field"
    >
      {fields.map((field) => (
        <MenuItem key={field.field_name} value={field.field_name}>
          {customFieldDisplayName(field)}
        </MenuItem>
      ))}
    </Select>
  );
}
